
import { tool } from "ai"
import { z } from "zod"
import { workerRegistry } from "@/lib/agents/registry"
import { AllAIModels, OpenAIModels } from "@/lib/agents/modellist"
import { supabase } from "@/lib/agents/db"
import { app } from "@/lib/app"
import { configureAgent } from "@/lib/agents/agentfactory"
import { AGENT_SETTINGS_CHANGED_EVENT, FLAG_ICONS, MAX_FLAGS, flagsOf } from "@/lib/agents/escalationFlags"

export interface AgentBuilderContext {
  getAgent: () => Agent
  /** Re-renders the canvas after a mutation. */
  refresh: () => void
  replaceAgent: (next: Agent) => void
  removeWorker: (workerId: string) => void
  teamId?: string
}

const COL_PITCH = 460
const ROW_PITCH = 620
const PER_ROW = 3

function nextPosition(agent: any): { x: number; y: number } {
  const placed = Object.values<any>(agent?.workers ?? {})
    .filter(w => typeof w.config?.x === "number")

  // First node of an empty canvas sits at the origin the editor opens on.
  if (!placed.length) return { x: 0, y: 0 }

  const grid = (agent as any).__builderGrid
  const stale = !grid || grid.total !== placed.length - grid.filled
  const g = stale
    ? {
      x: Math.min(...placed.map(w => w.config.x ?? 0)),
      y: Math.max(...placed.map(w => w.config.y ?? 0)) + ROW_PITCH,
      filled: 0,
      total: placed.length,
    }
    : grid

  const i = g.filled
  g.filled = i + 1
  ;(agent as any).__builderGrid = g

  return {
    x: g.x + (i % PER_ROW) * COL_PITCH,
    y: g.y + Math.floor(i / PER_ROW) * ROW_PITCH,
  }
}

const WIRING_NOTES: Record<string, string> = {
  onboard:
    "ASKS QUESTIONS OVER MANY TURNS, one per run, and is the responder until it "
    + "finishes. How it behaves:\n"
    + "- While asking: `output` is the next question, and `finished` is false.\n"
    + "- On the turn it completes: `output` becomes the user's own message passed "
    + "through, and `finished` is true — so downstream sees the real question, not "
    + "an onboarding prompt.\n"
    + "- `finished` is reset to false at the START of every run and set true only on "
    + "the completing turn, so it reflects this turn, not a stored flag.\n"
    + "WIRING THAT FOLLOWS:\n"
    + "- request.message -> onboard.input.\n"
    + "- onboard.output -> the response field, so the question reaches the user.\n"
    + "- To let an LLM agent take over afterwards: onboard.output -> agent.input, "
    + "AND gate that agent with ab_add_condition (boolean equals true) fed by "
    + "onboard.finished. Without the gate the agent runs every turn, costs a call, "
    + "and its output is merely overwritten.\n"
    + "- Wire the LLM agent's output to the SAME response field. An unset value does "
    + "not overwrite: the gated agent leaves it undefined while onboarding, so the "
    + "question shows, and its answer wins once the gate opens.\n"
    + "- Chat history: connect chatHistory to onboard.history and onboard RECORDS THE "
    + "TURNS ITSELF while asking. Do not also expect the LLM agent to record them — "
    + "it is not running during onboarding.",
  promptAgent:
    "Tools connect INTO its `tool` handle (agent.tool -> tool worker's tool handle); "
    + "a tool worker not wired there never runs, however well configured. "
    + "`instructions` is a FIELD, not a parameter — set it with ab_set_field_value. "
    + "Gate it with ab_add_condition when it should not run every turn.",
  chatHistory:
    "Its `output` is the conversation so far, and it is wired INTO whatever needs "
    + "the history — an LLM agent's `history`, or onboard's `history`. It has no "
    + "input handle: it reads from storage keyed by the run's conversation id, so a "
    + "run without one fails with \"No uuid provided\" rather than returning empty.",
}

const STRUCTURED_PARAMS: Record<string, { note: string; example: unknown }> = {
  onboard: {
    note:
      "The whole flow lives in `items`, asked in array order. RULES, each of which "
      + "silently breaks the flow when broken:\n"
      + "- `name` is REQUIRED and must be unique. It keys the stored answer, and an item "
      + "without one is skipped entirely and never asked (getNext ignores nameless items).\n"
      + "- `question` is the text the user sees.\n"
      + "- `options` is optional. With options, the answer must match a `label` EXACTLY "
      + "(compared lowercased, with square brackets stripped); anything else re-prompts "
      + "with `invalidOptionAnswer`. Without options, any free-text answer is accepted.\n"
      + "- Each option needs an `action`: \"continue\" stores the answer and moves on, "
      + "\"goto\" jumps to the item named in `goto`, \"reset\" clears every answer and "
      + "starts over. An option with no action does nothing at all.\n"
      + "- `goto` must name an existing item's `name`, or the flow stops there.\n"
      + "- `title` is the label used in the summary; `summarize: true` includes the answer "
      + "in the Summary output.\n"
      + "Write the entire `items` array in ONE ab_set_parameters call — it replaces the "
      + "key, so a second call with one item discards the rest.",
    example: {
      items: [
        {
          name: "language",
          title: "Preferred language",
          question: "Which language would you like to continue in?",
          summarize: true,
          options: [
            { label: "English", answer: "", action: "continue" },
            { label: "Arabic", answer: "", action: "continue" },
          ],
        },
        {
          name: "need",
          title: "What they need",
          question: "What do you need help with today?",
          summarize: true,
        },
        {
          name: "start_over",
          title: "Restart",
          question: "Would you like to start again?",
          options: [
            { label: "Yes", answer: "Starting over.", action: "reset" },
            { label: "No", answer: "", action: "goto", goto: "need" },
          ],
        },
      ],
      invalidOptionAnswer: "Please answer using one of the options shown.",
    },
  },
}

const MODEL_PARAMS: Record<string, Record<string, { value: string; label: string }[]>> = {
  promptAgent: {
    model: AllAIModels, fallbackModel: AllAIModels,
  },
  structured: {
    model: AllAIModels,
  },
  schema: {
    model: OpenAIModels.map(m => ({ ...m, value: m.value.replace("openai/", "") })),
  },
}

// Why a model-valued parameter is invalid for this worker type, with the closest real values, or null
export function checkModelParameters(workerType: string | undefined, parsed: Record<string, any>): string | null {
  const modelParams = workerType ? MODEL_PARAMS[workerType] : undefined
  if (!modelParams) return null
  for (const [key, value] of Object.entries(parsed)) {
    const options = modelParams[key]
    if (!options || options.some(o => o.value === value)) continue
    const needle = String(value ?? "").toLowerCase()
    const close = options.filter(o => needle && (o.value.toLowerCase().includes(needle) || o.label.toLowerCase().includes(needle)))
    const suggest = (close.length ? close : options.slice(0, 8)).slice(0, 8).map(o => o.value).join(", ")
    return `"${value}" is not a valid ${key} for ${workerType}. It must be one of the exact values in `
      + `ab_describe_worker_type("${workerType}").modelValues.${key}${close.length ? `; closest: ${suggest}` : ` (e.g. ${suggest}, …)`}. `
      + "Don't guess the format."
  }
  return null
}

const FIELD_WORKERS: Record<string, {
  direction: "input" | "output"
  needsPrompt?: boolean
  /** Types the node's AddFieldsForm hides, and so cannot be created. */
  ignore?: string[]
}> = {
  request: { direction: "output", ignore: ["references", "enum", "handoff"] },
  response: { direction: "input", ignore: ["enum", "handoff"] },
  schema: {
    direction: "output", needsPrompt: true,
    ignore: ["references", "doc", "chat", "json", "handoff"],
  },
  // Worker type is "structured", not "structuredOutput" -- the file is
  // structuredoutput.ts but the registry type differs.
  structured: {
    direction: "output", needsPrompt: true,
    ignore: ["references", "doc", "chat", "json", "handoff"],
  },
  template: {
    direction: "input",
    ignore: ["references", "doc", "chat", "json", "handoff"],
  },
  // state and chathistory import AddFieldsForm but never render it.
}

const FIELD_TYPES = [
  "string", "string[]", "number", "number[]", "boolean", "enum",
  "json", "doc", "chat", "audio", "date", "file",
] as const

const PROBE_CACHE = new Map<string, {
  shape: ReturnType<typeof describeHandles>
  parameters: Record<string, any>
  conditionable: boolean
}>()

function probeWorkerType(agent: Agent, reg: WorkerRegistryItem) {
  const key = reg.type
  const cached = PROBE_CACHE.get(key)
  if (cached) return cached

  let probe: any
  try {
    probe = reg.create(agent)
    const result = {
      shape: describeHandles(probe),
      parameters: probe.parameters ?? {},
      conditionable: !!probe.conditionable,
    }
    PROBE_CACHE.set(key, result)
    return result
  } catch (err: any) {
    return { error: `Could not inspect ${key}: ${err?.message ?? String(err)}` }
  } finally {
    if (probe?.config?.id) {
      delete probe.config.x
      delete probe.config.y
      agent.deleteWorker(probe.config.id)
    }
  }
}

const ok = (data: any) => ({ ok: true, ...data })
const fail = (error: string) => ({ ok: false, error })

function resolveWorker(ctx: AgentBuilderContext, workerId: string) {
  const agent = ctx.getAgent()
  if (!agent) return { error: "No agent is open in the editor." as const }
  const w = agent.workers?.[workerId]
  if (!w) return { error: `No worker with id ${workerId} on the canvas.` }
  return { agent, w }
}

function describeHandles(w: any) {
  const fields = w?.fields ?? {}
  const inputs: string[] = []
  const outputs: string[] = []
  for (const [name, io] of Object.entries<any>(fields)) {
    let entry = `${name}${io?.type ? `:${io.type}` : ""}`
    if (io?.direction !== "output" && io?.default) {
      const text = String(io.default)
      entry += ` = ${JSON.stringify(text.length > 60 ? `${text.slice(0, 60)}…` : text)}`
    }
    if (io?.direction === "output") outputs.push(entry)
    else inputs.push(entry)
  }
  const kind = w?.config?.type
  const spec = kind ? FIELD_WORKERS[kind] : undefined
  if (spec) {
    const note = !inputs.length && !outputs.length
      ? `This ${kind} node has no fields yet — its handles are yours to define. `
        + `Add them with ab_add_schema_field.`
      : `Fields on this ${kind} node are user-defined; add more with ab_add_schema_field.`
    return {
      inputs, outputs,
      schemaNote: spec.needsPrompt
        ? `${note} Each field needs a prompt describing what to extract.`
        : note,
    }
  }
  return { inputs, outputs }
}

export function makeAgentBuilderTools(ctx: AgentBuilderContext) {
  return {
    ab_list_worker_types: tool({
      description:
        "List every worker type that can be placed on the canvas, with its handles. " +
        "Call this BEFORE adding nodes — handle names are not guessable, and connecting " +
        "two workers requires the exact names on both sides.",
      parameters: z.object({
        category: z.string().nullable()
          .describe("Filter to one category, e.g. \"tool\" or \"generator\". Null for all."),
      }),
      execute: async ({ category }) => {
        const types = Object.entries<any>(workerRegistry)
          .filter(([, r]) => r && !r.deprecated)
          .filter(([, r]) => !category || r.category === category)
          .map(([key, r]) => ({
            type: r.type ?? key,
            title: r.title,
            category: r.category,
            description: r.description,
          }))
        return ok({ count: types.length, types })
      },
    }),

    ab_describe_worker_type: tool({
      description:
        "Show one worker type's handles and default parameters. Use this before connecting " +
        "to it, so the handle names in ab_connect are real rather than assumed.",
      parameters: z.object({
        type: z.string().describe("Worker type, e.g. \"cmssearch\" or \"promptAgent\""),
      }),
      execute: async ({ type }) => {
        const reg = (workerRegistry as any)[type]
        if (!reg) {
          const known = Object.keys(workerRegistry).join(", ")
          return fail(`No worker type "${type}". Available: ${known}`)
        }
        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")
        const probed = probeWorkerType(agent, reg)
        if ("error" in probed) return fail(probed.error)
        const { shape, parameters, conditionable } = probed

        // Model-valued parameters need their exact accepted values named
        // here, since ab_set_parameters rejects anything else and this is
        // the tool meant to be checked before calling it.
        const modelParams = MODEL_PARAMS[reg.type ?? type]
        const modelValues = modelParams
          ? Object.fromEntries(
              Object.entries(modelParams).map(([k, opts]) => [k, opts.map(o => o.value)]),
            )
          : undefined

        // A worker configured through a structured parameter needs its shape
        // spelled out; the defaults alone show an empty array.
        const structured = STRUCTURED_PARAMS[reg.type ?? type]

        return ok({
          type: reg.type,
          title: reg.title,
          category: reg.category,
          description: reg.description,
          ...shape,
          parameters,
          modelValues,
          // flag_id must name one of the agent's own flags
          flagIds: (reg.type ?? type) === "writeFlags" ? flagsOf(agent).map(f => f.id) : undefined,
          parameterShape: structured?.note,
          parameterExample: structured?.example,
          // Whether this worker can be gated. Reported because the condition
          // A condition handle does not exist until added, so its absence
          // from the handle list means nothing.
          // handle list says nothing about whether one is possible.
          wiring: WIRING_NOTES[reg.type ?? type],
          conditionable,
          conditionNote: conditionable
            ? "Can be gated with ab_add_condition, which adds a condition input handle to "
              + "connect the deciding value into."
            : "Cannot be gated; it has no condition support.",
        })
      },
    }),

    ab_get_canvas: tool({
      description:
        "Read the agent currently open in the editor: every node, its parameters, and every " +
        "connection. Call this first — it tells you what already exists so you extend the " +
        "graph rather than duplicating it.",
      parameters: z.object({}),
      execute: async () => {
        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")

        const workers = Object.values<any>(agent.workers).map(w => ({
          id: w.config.id,
          type: w.config.type,
          ...describeHandles(w),
          parameters: w.parameters ?? {},
        }))
        const handleName = (workerId: string, handleId: string) => {
          const w = agent.workers[workerId]
          const io = Object.values<any>(w?.fields ?? {}).find(f => f.id === handleId)
          return io?.name ?? handleId
        }
        const edges = Object.values<any>(agent.edges).map(e => ({
          from: `${e.source}.${handleName(e.source, e.sourceHandle)}`,
          to: `${e.target}.${handleName(e.target, e.targetHandle)}`,
        }))
        const issues: string[] = []
        const targetsOf = (id: string) =>
          edges.filter(e => e.from.startsWith(`${id}.`)).map(e => e.to)
        const feeds = (id: string) => edges.some(e => e.to.startsWith(`${id}.`))

        for (const w of workers) {
          const outs = targetsOf(w.id)
          const isTool = ["cmssearch", "vectorSearch", "search", "api",
            "apiConnector", "externalApiConnector", "lookup"].includes(w.type)

          const toolWired = edges.some(e =>
            e.to === `${w.id}.tool` || (e.from.startsWith(`${w.id}.`) && e.to.endsWith(".tool")))
          if (isTool && !toolWired) {
            issues.push(
              `${w.type} (${w.id}) is not wired to any agent's \`tool\` handle, so it will `
              + `never run. Connect the agent's \`tool\` output to this worker's \`tool\` input.`,
            )
          }
          if (w.type === "onboard") {
            if (!outs.length) {
              issues.push(
                `onboard (${w.id}) has no outgoing edges — its questions reach nobody. `
                + `Wire \`output\` to the response field.`,
              )
            }
            if (!feeds(w.id)) {
              issues.push(
                `onboard (${w.id}) has no input — it will never see the user's message. `
                + `Wire request.message to its \`input\`.`,
              )
            }
            // An ungated agent fed by onboard runs during onboarding too.
            for (const t of outs) {
              const targetId = t.split(".")[0]
              const target = workers.find(x => x.id === targetId)
              if (target?.type !== "promptAgent") continue
              const gated = target.inputs?.some((h: string) => h.startsWith("condition"))
              if (!gated) {
                issues.push(
                  `${target.type} (${targetId}) is fed by onboard but not gated, so it runs `
                  + `during onboarding and its output is only overwritten. Add a condition `
                  + `with ab_add_condition and connect onboard's \`finished\` to it.`,
                )
              }
            }
          }
        }

        return ok({
          title: agent.title,
          workerCount: workers.length,
          workers,
          edges,
          issues: issues.length ? issues : undefined,
          note: workers.length === 0 ? "The canvas is empty." : undefined,
        })
      },
    }),

    ab_list_agents: tool({
      description:
        "List the team's other agents. Use this to find a working example of the pattern you " +
        "are about to build — copying a graph that already runs beats inventing one.",
      parameters: z.object({}),
      execute: async () => {
        if (!ctx.teamId) return fail("No team selected.")
        const { data, error } = await supabase
          .from("agents")
          .select("id, title, description, type")
          .eq("team_id", ctx.teamId)
          .order("id", { ascending: false })
          .limit(100)
        if (error) return fail(error.message)
        return ok({ count: data?.length ?? 0, agents: data ?? [] })
      },
    }),

    ab_get_agent: tool({
      description:
        "Read another agent's full graph — its workers, their parameters, and how they are " +
        "wired. This is how you learn an existing pattern before reproducing it.",
      parameters: z.object({
        id: z.number().describe("Agent id from ab_list_agents"),
      }),
      execute: async ({ id }) => {
        if (!ctx.teamId) return fail("No team selected.")
        const { data, error } = await supabase
          .from("agents")
          .select("id, title, description, workers, edges")
          .eq("id", id)
          .eq("team_id", ctx.teamId)
          .single()
        if (error) return fail(error.message)

        const workerList: any[] = Array.isArray(data.workers)
          ? data.workers
          : Object.values(
            data.workers && typeof data.workers === "object" ? data.workers as any : {},
          )
        const edgeRecord: Record<string, any> =
          data.edges && typeof data.edges === "object" && !Array.isArray(data.edges)
            ? data.edges as any
            : {}

        // Handle ids are what edges reference, so a listing without handles
        // match them to the wiring.
        const handleName = (workerId: string, handleId: string) => {
          const w = workerList.find(x => x.id === workerId)
          const h = Object.values<any>(w?.handles ?? {}).find(x => x.id === handleId)
          return h?.name ?? handleId
        }

        const workers = workerList.map(w => ({
          id: w.id,
          type: w.type,
          parameters: w.parameters ?? {},
          fields: Object.values<any>(w.handles ?? {}).map(h => ({
            name: h.name,
            direction: h.direction,
            type: h.type,
            // The prompt/instructions text lives on a handle's default.
            ...(h.default ? { value: h.default } : {}),
          })),
        }))
        const edges = Object.values<any>(edgeRecord).map(e => ({
          from: `${e.source}.${handleName(e.source, e.sourceHandle)}`,
          to: `${e.target}.${handleName(e.target, e.targetHandle)}`,
        }))
        return ok({
          id: data.id, title: data.title, description: data.description, workers, edges,
          next: "To reproduce this agent on the canvas, use ab_copy_agent rather than "
            + "rebuilding it node by node.",
        })
      },
    }),

    ab_copy_agent: tool({
      description:
        "Copy another team agent onto this canvas, as a starting template. REPLACES "
        + "everything currently here — use it when the user asks to start from an existing "
        + "agent, or to overwrite this one with a copy of another. Reproduces the graph "
        + "exactly: every node, its parameters and prompts, and all the wiring. "
        + "\n\nPrefer this over rebuilding node by node: the wiring references internal "
        + "handle ids, and a hand-rebuilt copy loses them. "
        + "\n\nThe copy is NOT saved — the user reviews it and presses Save, and this agent "
        + "keeps its own id and title, so saving does not overwrite the agent copied FROM. "
        + "Confirm with the user before calling this if they have unsaved work here.",
      parameters: z.object({
        id: z.number().describe("Agent id from ab_list_agents."),
        keepTitle: z.boolean()
          .describe(
            "True keeps this agent's current title and description; false takes the "
            + "source agent's. Default to true — a copy that renames itself is confusing.",
          ),
      }),
      execute: async ({ id, keepTitle }) => {
        if (!ctx.teamId) return fail("No team selected.")
        const current = ctx.getAgent()
        if (!current) return fail("No agent is open in the editor.")

        const { data, error } = await supabase
          .from("agents")
          .select("id, title, description, type, workers, edges, config")
          .eq("id", id)
          .eq("team_id", ctx.teamId)
          .single()
        if (error) return fail(error.message)
        if (!data) return fail(`No agent with id ${id} on this team.`)

        const workerList: any[] = Array.isArray(data.workers) ? data.workers : []
        if (!workerList.length) {
          return fail(
            `Agent ${id} ("${data.title}") has no workers saved, so there is nothing to copy.`,
          )
        }

        let next: Agent
        try {
          next = configureAgent({
            title: keepTitle ? current.title : data.title,
            description: keepTitle ? current.description : (data.description ?? ""),
            type: data.type ?? current.type,
            workers: data.workers as any,
            edges: (data.edges ?? {}) as any,
            config: (data.config ?? {}) as any,
            team_id: (current as any).team_id ?? ctx.teamId,
            debuguuid: current.debuguuid,
          } as any)
        } catch (err: any) {
          return fail(`Could not rebuild agent ${id}: ${err?.message ?? String(err)}`)
        }

        next.id = current.id
        next.versions = current.versions
        if (keepTitle) {
          next.title = current.title
          next.description = current.description
        }

        ctx.replaceAgent(next)

        const copied = Object.keys(next.workers ?? {}).length
        const wired = Object.keys(next.edges ?? {}).length
        const types = [...new Set(workerList.map(w => w.type))].sort()
        return ok({
          from: { id: data.id, title: data.title },
          workers: copied,
          edges: wired,
          types,
          summary: `${data.title}: ${copied} nodes, ${wired} edges`,
          next: "The canvas now shows the copy. It is UNSAVED — tell the user to review it "
            + "and press Save, and that saving writes to THIS agent, not the one copied from.",
        })
      },
    }),

    ab_add_worker: tool({
      description:
        "Place a new worker on the canvas and return its id and handle names. " +
        "The node appears immediately; nothing is saved to the database until the user " +
        "presses Save.",
      parameters: z.object({
        type: z.string().describe("Worker type from ab_list_worker_types"),
      }),
      execute: async ({ type }) => {
        const reg = (workerRegistry as any)[type]
        if (!reg) return fail(`No worker type "${type}". Call ab_list_worker_types first.`)

        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")

        const w = reg.create(agent)
        // Without a position the node exists in the graph but renders
        const { x, y } = nextPosition(agent)
        w.config.x = x
        w.config.y = y
        // Every other mutating tool bumps the version counter the node
        // components memoize on; this one created a whole worker and bumped
        // nothing, leaving a following ab_connect to render against stale state.
        agent.updateWorkers?.()
        ctx.refresh()
        return ok({
          id: w.config.id,
          type: reg.type,
          position: { x, y },
          ...describeHandles(w),
          // Surfaced on placement too: a worker is often added without
          // describing it first, and this is exactly when its wiring
          // contract needs to be known.
          wiring: WIRING_NOTES[reg.type],
          next: "Use ab_connect with these handle names to wire it up.",
        })
      },
    }),

    ab_connect: tool({
      description:
        "Connect one worker's output handle to another's input handle. Both handle names must " +
        "exist — check with ab_get_canvas or ab_add_worker's response, since a wrong name " +
        "silently produces a graph that does not run.",
      parameters: z.object({
        sourceId: z.string().describe("Id of the worker the data comes FROM"),
        sourceHandle: z.string().describe("Output handle name on the source"),
        targetId: z.string().describe("Id of the worker the data goes TO"),
        targetHandle: z.string().describe("Input handle name on the target"),
      }),
      execute: async ({ sourceId, sourceHandle, targetId, targetHandle }) => {
        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")

        const source = agent.workers[sourceId]
        const target = agent.workers[targetId]
        if (!source) return fail(`No worker with id ${sourceId} on the canvas.`)
        if (!target) return fail(`No worker with id ${targetId} on the canvas.`)

        // looks right on the canvas and never carries a value.
        const handleError = (w: any, handle: string, which: string) => {
          const names = Object.keys(w.fields ?? {})
          const kind = w.config.type
          if (!names.length && (kind === "request" || kind === "response")) {
            return fail(
              `The ${kind} node has no fields yet. It is a schema you define: `
              + `add fields with ab_add_schema_field (worker ${w.config.id}), `
              + `then connect to them by name.`,
            )
          }
          if (!names.length) return fail(`${kind} has no ${which} handles.`)
          return fail(`${kind} has no handle "${handle}". It has: ${names.join(", ")}`)
        }
        const sourceIO = source.fields?.[sourceHandle]
        const targetIO = target.fields?.[targetHandle]
        if (!sourceIO) return handleError(source, sourceHandle, "source")
        if (!targetIO) return handleError(target, targetHandle, "target")

        if (!sourceIO.id || !targetIO.id) {
          return fail(
            `Handle "${!sourceIO.id ? sourceHandle : targetHandle}" has no id yet; `
            + `re-read the canvas with ab_get_canvas and retry.`,
          )
        }
        const isToolWiring = sourceIO.type === "tool" && targetIO.type === "tool"

        // Otherwise direction is checked, because React Flow enforces it
        if (!isToolWiring && sourceIO.direction !== "output") {
          return fail(
            `"${sourceHandle}" on ${source.config.type} is an input, so it cannot be the `
            + `source of an edge. Edges run output -> input; you may have the two ends swapped.`,
          )
        }
        if (!isToolWiring && targetIO.direction !== "input") {
          return fail(
            `"${targetHandle}" on ${target.config.type} is an output, so it cannot be the `
            + `target of an edge. Edges run output -> input; you may have the two ends swapped.`,
          )
        }

        const id = `${sourceId}-${sourceIO.id}-${targetId}-${targetIO.id}`
        agent.edges[id] = {
          source: sourceId,
          target: targetId,
          sourceHandle: sourceIO.id,
          targetHandle: targetIO.id,
        }
        agent.updateWorkers?.()
        ctx.refresh()

        return ok({
          edgeId: id,
          summary: `${source.config.type}.${sourceHandle} → ${target.config.type}.${targetHandle}`,
        })
      },
    }),

    ab_set_parameters: tool({
      description:
        "Set parameters on a worker already on the canvas — a prompt, a model, a tool " +
        "description. Pass them as a JSON object string; only the keys you supply change.",
      parameters: z.object({
        workerId: z.string(),
        parametersJson: z.string()
          .describe("JSON object of parameters, e.g. '{\"maxResults\":10}'"),
      }),
      execute: async ({ workerId, parametersJson }) => {
        const r = resolveWorker(ctx, workerId)
        if ("error" in r) return fail(r.error)
        const { agent, w } = r

        let parsed: any
        try {
          parsed = JSON.parse(parametersJson)
        } catch {
          return fail("parametersJson is not valid JSON.")
        }
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          return fail("parametersJson must be a JSON object.")
        }

        if (w.config?.type === "onboard" && Array.isArray((parsed as any).items)) {
          const items: any[] = (parsed as any).items
          const problems: string[] = []
          const names = new Set(items.map(it => it?.name).filter(Boolean))

          items.forEach((it, idx) => {
            const where = it?.name ? `"${it.name}"` : `item ${idx + 1}`
            if (!it || typeof it !== "object") {
              problems.push(`${where} is not an object.`)
              return
            }
            if (!it.name) {
              problems.push(
                `${where} has no "name". Nameless items are skipped and never asked.`,
              )
            }
            if (!it.question) problems.push(`${where} has no "question" to ask.`)
            if (it.options != null && !Array.isArray(it.options)) {
              problems.push(`${where}: "options" must be an array.`)
            }
            for (const [oi, o] of (Array.isArray(it.options) ? it.options : []).entries()) {
              const oWhere = `${where} option ${oi + 1}`
              if (!o?.label) problems.push(`${oWhere} has no "label" to match against.`)
              if (!o?.action) {
                problems.push(
                  `${oWhere} has no "action" — choosing it would do nothing. Use `
                  + `"continue", "goto" or "reset".`,
                )
              }
              if (o?.action === "goto" && !names.has(o?.goto)) {
                problems.push(
                  `${oWhere} goes to "${o?.goto ?? ""}", which is not the name of any item.`,
                )
              }
            }
          })

          const dupes = items.map(it => it?.name).filter(Boolean)
            .filter((n, i, all) => all.indexOf(n) !== i)
          for (const d of new Set(dupes)) {
            problems.push(`Two items are both named "${d}"; names must be unique.`)
          }

          if (problems.length) {
            return fail(
              `The onboarding flow would not work:\n- ${problems.join("\n- ")}\n\n`
              + `Nothing was changed. Call ab_describe_worker_type for "onboard" to see the `
              + `expected shape, then send the whole corrected items array.`,
            )
          }
        }

        const asFields = Object.keys(parsed).filter(k => w.fields?.[k])
        if (asFields.length) {
          return fail(
            `${asFields.join(", ")} ${asFields.length > 1 ? "are handles" : "is a handle"} on `
            + `${w.config.type}, not a parameter. Set ${asFields.length > 1 ? "them" : "it"} `
            + `with ab_set_field_value, which is what the node's own text box writes to.`,
          )
        }

        const invalid = checkModelParameters(w.config.type, parsed) ?? checkFlagParameter(agent, w.config.type, parsed)
        if (invalid) return fail(invalid)

        w.parameters = { ...(w.parameters ?? {}), ...parsed }

        // onboard.execute writes each answer to worker.fields[item.name], and
        // that handle is otherwise only created by the node's "add item"
        // button, so items written here need one too. Mirrors addItem and
        // handleNameChange in nodes/onboard.tsx.
        if (w.config?.type === "onboard" && Array.isArray((parsed as any).items)) {
          for (const it of (parsed as any).items as any[]) {
            if (!it?.name) continue
            const existing = w.fields?.[it.name]
            if (existing) {
              w.updateHandler?.(existing.id, { name: it.name, title: it.title || it.name })
              continue
            }
            w.addHandler({
              id: it.id || w.createHandlerId(),
              name: it.name,
              title: it.title || it.name,
              type: "string",
              direction: "output",
            } as any)
          }
          agent.updateWorkers?.()
        }

        w.updateWorker?.()
        ctx.refresh()
        return ok({
          workerId,
          applied: Object.keys(parsed),
          parameters: w.parameters,
          summary: Object.keys(parsed).join(", "),
        })
      },
    }),

    ab_set_field_value: tool({
      description:
        "Set the value of an INPUT handle that is not connected to anything — this is how "
        + "you write an LLM agent's instructions/system prompt, a template's text, or any "
        + "field shown as a text box on the node. Config is split: things like the model live "
        + "in parameters (ab_set_parameters), but anything that is also a handle, instructions "
        + "above all, lives here. Setting instructions through ab_set_parameters does nothing. "
        + "A connected handle takes its value from the edge, so do not set both.",
      parameters: z.object({
        workerId: z.string(),
        field: z.string().describe('Handle name, e.g. "instructions".'),
        value: z.string().describe("The text to place in the field."),
      }),
      execute: async ({ workerId, field, value }) => {
        const r = resolveWorker(ctx, workerId)
        if ("error" in r) return fail(r.error)
        const { agent, w } = r

        const io = w.fields?.[field]
        if (!io) {
          const inputs = Object.values<any>(w.fields ?? {})
            .filter(f => f.direction === "input").map(f => f.name)
          return fail(
            `${w.config.type} has no field "${field}". Its input fields are: `
            + `${inputs.join(", ") || "(none)"}.`,
          )
        }
        if (io.direction !== "input") {
          return fail(`"${field}" is an output; only input fields hold a value.`)
        }
        if (io.system || io.condition) {
          return fail(
            `"${field}" is a system handle on ${w.config.type}, not a field that holds a `
            + `value. Connect a value into it with ab_connect instead — writing one here `
            + `would be used whenever nothing is connected, which for a condition means the `
            + `gate never closes.`,
          )
        }

        io.default = value

        // believe it configured something that will be ignored.
        const connected = Object.values<any>(agent.edges)
          .some(e => e.target === workerId && e.targetHandle === io.id)

        w.updateWorker?.()
        ctx.refresh()
        return ok({
          workerId,
          field,
          chars: value.length,
          summary: `${field} set (${value.length} chars)`,
          warning: connected
            ? `"${field}" is also connected to an edge, and the edge value wins at run time. `
              + `Disconnect it if you want this text used.`
            : null,
        })
      },
    }),

    ab_add_condition: tool({
      description:
        "Gate a worker so it only runs when a condition holds. This is the graph-level way "
        + "to stop a node executing — an LLM agent that should stay quiet until onboarding "
        + "finishes, for example. Without it the node runs every turn and its output is "
        + "merely overwritten downstream, which costs a model call and can leak the wrong "
        + "answer."
        + "\n\nAdds a condition INPUT handle, which you then connect like any other: wire the "
        + "value that decides it (onboard's `finished`, say) into this handle with ab_connect. "
        + "A worker with several conditions runs when ANY of them holds."
        + "\n\nNot every worker supports this; ab_describe_worker_type says which do.",
      parameters: z.object({
        workerId: z.string().describe("The worker to gate."),
        type: z.enum(["boolean", "string", "number", "string[]", "enum"])
          .describe(
            "Type of the value being tested. MUST match the handle you will connect — the "
            + "evaluator branches on this, and a mismatch makes the condition never hold. "
            + "onboard's `finished` is boolean.",
          ),
        operator: z.enum([
          "equals", "notEquals", "contains", "notContains",
          "isEmpty", "isNotEmpty", "gt", "lt", "gte", "lte", "between",
        ]).describe(
          'How to compare. boolean: equals/notEquals. string: equals, notEquals, contains, '
          + 'notContains, isEmpty, isNotEmpty. number: equals, notEquals, gt, lt, gte, lte, '
          + 'between. string[]: isEmpty, isNotEmpty only.',
        ),
        value: z.string().nullable()
          .describe(
            'What to compare against. For boolean pass "true" or "false". Null for isEmpty '
            + "and isNotEmpty, which test the incoming value alone.",
          ),
        value2: z.string().nullable()
          .describe('Upper bound for "between". Null otherwise.'),
      }),
      execute: async ({ workerId, type, operator, value, value2 }) => {
        const r = resolveWorker(ctx, workerId)
        if ("error" in r) return fail(r.error)
        const { agent, w } = r

        if (!w.conditionable) {
          return fail(
            `${w.config.type} cannot be gated — it does not declare conditionable, and the `
            + `editor offers no "Add Condition" for it either. Gate a worker upstream of it, `
            + `or leave it ungated.`,
          )
        }

        const allowed: Record<string, string[]> = {
          boolean: ["equals", "notEquals"],
          string: ["equals", "notEquals", "contains", "notContains", "isEmpty", "isNotEmpty"],
          enum: ["equals", "notEquals", "contains", "notContains", "isEmpty", "isNotEmpty"],
          "string[]": ["isEmpty", "isNotEmpty"],
          number: ["equals", "notEquals", "gt", "lt", "gte", "lte", "between"],
        }
        if (!allowed[type]?.includes(operator)) {
          return fail(
            `"${operator}" is not a valid operator for a ${type} condition. Valid: `
            + `${allowed[type].join(", ")}.`,
          )
        }
        if (operator === "between" && !value2?.trim()) {
          return fail('"between" needs value2 as the upper bound.')
        }
        const needsValue = !["isEmpty", "isNotEmpty"].includes(operator)
        if (needsValue && !value?.trim()) {
          return fail(`"${operator}" needs a value to compare against.`)
        }

        // Booleans and numbers are compared with == against the incoming
        // value, so a string "true" would never equal a real boolean.
        const coerce = (v: string | null) => {
          if (v == null) return undefined
          if (type === "boolean") return v.trim().toLowerCase() === "true"
          if (type === "number") return Number(v)
          return v
        }

        const id = w.createHandlerId()
        w.addHandler({
          id,
          name: `condition_${id}`,
          title: "Condition",
          type: type as any,
          direction: "input",
          system: true,
          condition: true,
          operator: operator as any,
          conditionValue1: coerce(value),
          ...(operator === "between" ? { conditionValue2: coerce(value2) } : {}),
        } as any)

        agent.updateWorkers?.()
        ctx.refresh()

        const existing = w.handlersArray.filter((h: any) => h.condition).length
        return ok({
          workerId,
          handle: `condition_${id}`,
          summary: `${w.config.type} gated: ${type} ${operator}${value ? ` "${value}"` : ""}`,
          conditionsOnWorker: existing,
          next: `Now connect the deciding value into "condition_${id}" with ab_connect — `
            + `until something is wired to it the condition tests nothing and the worker `
            + `will not run.`
            + (existing > 1
              ? ` NOTE: this worker has ${existing} conditions and runs when ANY holds.`
              : ""),
        })
      },
    }),

    ab_add_schema_field: tool({
      description:
        // offered.
        `Add a field to a worker whose fields the user defines: `
        + `${Object.keys(FIELD_WORKERS).join(", ")}. Each field becomes a connectable handle `
        + `of the same name. Direction is implied by the worker, so you do not pass it. For `
        + `${Object.entries(FIELD_WORKERS).filter(([, s]) => s.needsPrompt).map(([k]) => k).join(" and ")}, `
        + `ALWAYS pass a prompt — it is the description the extracting model reads, and `
        + `without it extraction is guesswork.`,
      parameters: z.object({
        workerId: z.string(),
        name: z.string().describe("Field name. Becomes the handle name used by ab_connect."),
        type: z.enum(FIELD_TYPES).describe("Data type of the field."),
        prompt: z.string().nullable()
          .describe(
            "For Schema/Structured Output: what this field should contain, written for the "
            + "model that extracts it. Null for other workers, which ignore it.",
          ),
        enumValues: z.string().nullable()
          .describe('Comma-separated options when type is "enum". Null otherwise.'),
      }),
      execute: async ({ workerId, name, type, prompt, enumValues }) => {
        const r = resolveWorker(ctx, workerId)
        if ("error" in r) return fail(r.error)
        const { agent, w } = r

        const kind = w.config.type
        const spec = FIELD_WORKERS[kind]
        if (!spec) {
          return fail(
            `${kind} does not take user-defined fields; its handles are fixed. `
            + `Use ab_describe_worker_type to see them. Workers that do: `
            + `${Object.keys(FIELD_WORKERS).join(", ")}.`,
          )
        }
        // A duplicate name shadows the existing handle in `fields`, making one
        // of the two unreachable by ab_connect. AddFieldsForm rejects it too.
        if (w.fields?.[name]) return fail(`${kind} already has a field named "${name}".`)

        if (spec.ignore?.includes(type)) {
          const allowed = FIELD_TYPES.filter(t => !spec.ignore!.includes(t))
          return fail(
            `${kind} does not support "${type}" fields. Allowed: ${allowed.join(", ")}.`,
          )
        }
        if (type === "enum" && !enumValues?.trim()) {
          return fail('An enum field needs enumValues, e.g. "yes,no,maybe".')
        }
        if (spec.needsPrompt && !prompt?.trim()) {
          return fail(
            `${kind} fields need a prompt: it becomes the field's description for the `
            + `extracting model, and an empty one makes extraction unreliable.`,
          )
        }

        const io: any = {
          name,
          type,
          prompt: prompt?.trim() || undefined,
          enum: type === "enum"
            ? enumValues!.split(",").map(v => v.trim()).filter(Boolean)
            : undefined,
        }

        w.addHandler({ ...io, direction: spec.direction })

        agent.updateWorkers?.()
        ctx.refresh()
        return ok({
          workerId,
          field: name,
          direction: spec.direction,
          summary: `${name}: ${type}`,
          next: `Connect using handle name "${name}".`,
        })
      },
    }),

    ab_reset_conversation: tool({
      description:
        "Start a fresh test conversation: subsequent ab_run_agent calls get a new "
        + "conversation id, so Chat History sees no earlier turns. Use this between "
        + "unrelated test scenarios, or when earlier turns are skewing what you are trying "
        + "to observe — a five-round test of one topic tells you nothing if the first two "
        + "rounds were about something else."
        + "\n\nBy default this only switches identity; the old conversation's rows are left "
        + "alone, orphaned but intact. Pass deleteHistory:true to actually delete the stored "
        + "history, state and messages for the CURRENT conversation first — destructive and "
        + "not undoable, so only when the user asks to clear the data itself.",
      parameters: z.object({
        deleteHistory: z.boolean()
          .describe(
            "False starts a new conversation and leaves the old rows in place — the safe "
            + "default. True permanently deletes the current conversation's stored history, "
            + "state and messages before switching.",
          ),
      }),
      execute: async ({ deleteHistory }) => {
        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")

        const previous: string | undefined = (agent as any).builderTestUid

        if (deleteHistory) {
          if (!previous) {
            return fail(
              "No test conversation has run yet, so there is nothing to delete. Call this "
              + "with deleteHistory:false to start one, or just run the agent.",
            )
          }
          try {
            await agent.resetAgent(previous)
          } catch (err: any) {
            return fail(`Could not delete the stored conversation: ${err?.message ?? String(err)}`)
          }
        }

        const next = crypto.randomUUID()
        ;(agent as any).builderTestUid = next
        ;(agent as any).builderTestState = { agent: {}, workers: {} }

        return ok({
          conversationId: next,
          previousConversationId: previous ?? null,
          deletedHistory: !!deleteHistory,
          summary: deleteHistory
            ? "History deleted, new conversation started"
            : "New conversation started",
          next: deleteHistory
            ? "The previous conversation's stored rows are gone. Runs from here start clean."
            : "Runs from here start clean. The previous conversation's rows still exist, "
              + "unreferenced — say so if the user expected them deleted.",
        })
      },
    }),

    ab_run_agent: tool({
      description:
        "Run the agent on the canvas with a test message and return what happened: the "
        + "workers that executed in order, any worker that errored, and the final output. "
        + "Use this to CHECK YOUR WORK after building — a graph that looks right can still "
        + "fail because a handle is unwired or a prompt is empty, and this is the only way "
        + "to find that out. Runs the unsaved canvas, so it tests your edits, not what is "
        + "in the database.",
      parameters: z.object({
        message: z.string().describe("The test message to send, as an end user would."),
      }),
      execute: async ({ message }) => {
        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")

        const warnings: string[] = []
        if (!agent.debuguuid) {
          warnings.push(
            "The agent has no debug uuid of its own, so these runs share a temporary "
            + "conversation id minted for this editor session. History works ACROSS runs "
            + "here, but it is not the id production would use, and it is lost when the "
            + "page reloads. Saving the agent assigns a permanent one.",
          )
        }

        const apiKeys = await app.fetchAPIkeys(ctx.teamId)

        const inputWorker = agent.getInputWorker?.()
        const outputs = Object.values<any>(inputWorker?.fields ?? {})
          .filter(f => f.direction === "output")
        const messageField = outputs.find(f => f.name === "message" && f.type === "string")
          ?? outputs.find(f => f.type === "string")

        if (inputWorker && !messageField) {
          return ok({
            ran: false,
            error: "The Input node has no string output field to receive the test message.",
            executed: [],
            summary: "Cannot run: Input node has no usable field",
          })
        }

        const inputPayload: Record<string, any> = { history: [] }
        if (messageField) inputPayload[messageField.name] = message
        // A non-"message" field works for this run, but agent.hasChatShape
        // only recognises "message".
        // but the platform's own shape check (agent.hasChatShape) only
        // blocking the run over.
        const nonStandardName = messageField && messageField.name !== "message"

        const trace: { type: string; id: string }[] = []
        const testUid: string = ((agent as any).builderTestUid ??=
          agent.debuguuid || crypto.randomUUID())
        const testState = ((agent as any).builderTestState ??= { agent: {}, workers: {} })

        const p: AgentParameters = {
          debug: true, // Lets request handles fall back to their mock values.
          input: inputPayload,
          apiKeys,
          uid: testUid,
          state: testState as any,
          logWriter: ({ worker }) => {
            trace.push({ type: worker.config.type, id: worker.config.id })
          },
        }

        try {
          agent.reset()
          await agent.execute(p)
        } catch (err: any) {
          return ok({
            ran: false,
            error: err?.message ?? String(err),
            executed: trace.map(t => t.type),
            summary: `Run threw: ${err?.message ?? "unknown error"}`,
          })
        }

        // Per-worker errors do not throw; they are left on the worker.
        const failed = Object.values<any>(agent.workers)
          .filter(w => w.error)
          .map(w => ({ worker: w.config.type, id: w.config.id, error: String(w.error) }))

        const executed = trace.map(t => t.type)
        const ok_ = !p.error && !failed.length

        // Named so the model can see whether the response node produced
        const output = p.output ?? {}
        const empty = ok_ && !Object.keys(output).length

        const noTeam = !p.team
        if (noTeam) {
          warnings.push(
            "This run had NO TEAM, so every team-scoped tool refused its calls — the CMS "
            + "Navigator answers \"REFUSED — no team\" to everything. The agent's team comes "
            + "from its saved record, so this usually means the agent has never been saved. "
            + "Tell the user to save it and run again; nothing about the graph is at fault.",
          )
        }

        return ok({
          ran: true,
          succeeded: ok_,
          executed,
          error: p.error ?? null,
          failedWorkers: failed,
          output,
          // Reported so a multi-round test is verifiable: runs sharing an id
          // build on each other, and a changed id means history started over.
          conversationId: testUid,
          warnings: warnings.length ? warnings : null,
          diagnosis: noTeam
            ? "The run had no team, so team-scoped tools could not run. Fix that before "
              + "reading anything else into the result."
            : empty
            ? "The run finished with no error but produced no output. The response node's "
              + "fields are probably unconnected, so nothing reached it."
            : !executed.length
              ? "No worker executed. There is likely no request/input node, or nothing is "
                + "connected to it."
              : null,
          summary: ok_
            ? (empty ? `Ran ${executed.length} workers, no output` : `Ran ${executed.length} workers OK`)
            : `Failed after ${executed.length}: ${p.error ?? failed[0]?.error ?? "unknown"}`,
        })
      },
    }),

    ab_remove_schema_field: tool({
      description:
        "Remove a field you or the user added to a request (Input), response (Output), "
        + "Schema, Structured Output or Template node. Also removes any edge attached to it, "
        + "since an edge pointing at a deleted handle would be invisible on the canvas but "
        + "still stored. Cannot remove a worker's fixed handles (input/output/tool and "
        + "similar) — those are not user-defined fields.",
      parameters: z.object({
        workerId: z.string(),
        field: z.string().describe("The field name to remove, as shown by ab_get_canvas."),
      }),
      execute: async ({ workerId, field }) => {
        const r = resolveWorker(ctx, workerId)
        if ("error" in r) return fail(r.error)
        const { agent, w } = r

        const kind = w.config.type
        if (!FIELD_WORKERS[kind]) {
          return fail(`${kind} has no user-defined fields to remove; its handles are fixed.`)
        }

        const io = w.fields?.[field]
        if (!io) {
          const names = Object.keys(w.fields ?? {})
          return fail(`${kind} has no field "${field}". It has: ${names.join(", ") || "(none)"}`)
        }
        if ((io as any).system) {
          return fail(`"${field}" is a fixed handle on ${kind}, not a field you added — it cannot be removed.`)
        }

        // Drop edges pointing at the removed handle.
        const dropped: string[] = []
        for (const [id, e] of Object.entries<any>(agent.edges)) {
          if (e.sourceHandle === io.id || e.targetHandle === io.id) {
            delete agent.edges[id]
            dropped.push(id)
          }
        }

        w.deleteHandler(io.id)
        agent.updateWorkers?.()
        ctx.refresh()
        return ok({ workerId, removed: field, edgesRemoved: dropped.length })
      },
    }),

    ab_disconnect: tool({
      description:
        "Remove one edge between two workers, leaving both nodes in place. Use this to "
        + "rewire a connection or undo a mistaken one — ab_remove_worker deletes the whole "
        + "node, which loses more than intended.",
      parameters: z.object({
        sourceId: z.string(),
        sourceHandle: z.string().describe("Field name on the source, as shown by ab_get_canvas."),
        targetId: z.string(),
        targetHandle: z.string().describe("Field name on the target, as shown by ab_get_canvas."),
      }),
      execute: async ({ sourceId, sourceHandle, targetId, targetHandle }) => {
        const agent = ctx.getAgent()
        if (!agent) return fail("No agent is open in the editor.")

        const source = agent.workers[sourceId]
        const target = agent.workers[targetId]
        if (!source) return fail(`No worker with id ${sourceId} on the canvas.`)
        if (!target) return fail(`No worker with id ${targetId} on the canvas.`)

        // Edges are stored by handle id, not name (see ab_connect) — resolved
        // the same way before searching for the edge to remove.
        const sourceIO = source.fields?.[sourceHandle]
        const targetIO = target.fields?.[targetHandle]
        if (!sourceIO) return fail(`${source.config.type} has no field "${sourceHandle}".`)
        if (!targetIO) return fail(`${target.config.type} has no field "${targetHandle}".`)

        const removed: string[] = []
        for (const [id, e] of Object.entries<any>(agent.edges)) {
          if (
            e.source === sourceId && e.sourceHandle === sourceIO.id
            && e.target === targetId && e.targetHandle === targetIO.id
          ) {
            delete agent.edges[id]
            removed.push(id)
          }
        }
        if (!removed.length) {
          return fail(
            `No edge from ${sourceId}.${sourceHandle} to ${targetId}.${targetHandle}. `
            + `Check ab_get_canvas — it may be the reverse direction.`,
          )
        }

        agent.updateWorkers?.()
        ctx.refresh()
        return ok({ removed: removed.length })
      },
    }),

    ab_remove_worker: tool({
      description:
        "Remove a worker from the canvas, along with every edge touching it. Use only for " +
        "something you added by mistake — removing a node the user placed loses their work.",
      parameters: z.object({
        workerId: z.string(),
      }),
      execute: async ({ workerId }) => {
        const agent = ctx.getAgent()
        const worker = agent?.workers?.[workerId]
        if (!worker) return fail(`No worker with id ${workerId}.`)
        const type = worker.config?.type

        // Drop edges touching this worker; they would outlive it.
        // references a node that is gone -- which React Flow reports as
        // "Couldn't create edge for target handle id".
        const dropped: string[] = []
        for (const [id, e] of Object.entries<any>(agent.edges)) {
          if (e.source === workerId || e.target === workerId) {
            delete agent.edges[id]
            dropped.push(id)
          }
        }

        // The workers at the far end of the dropped edges need repainting;
        // removeWorker only concerns the node being deleted.
        agent.updateWorkers?.()
        // Takes the node off the canvas before deleting the worker; doing it
        ctx.removeWorker(workerId)
        return ok({
          removed: workerId,
          type,
          edgesRemoved: dropped.length,
          summary: `${type ?? "worker"} removed, ${dropped.length} edge${dropped.length === 1 ? "" : "s"} dropped`,
        })
      },
    }),
  }
}

export const AGENT_BUILDER_TOOL_LABELS: Record<string, string> = {
  ab_list_worker_types: "List Worker Types",
  ab_describe_worker_type: "Describe Worker Type",
  ab_get_canvas: "Read Canvas",
  ab_list_agents: "List Agents",
  ab_get_agent: "Read Agent",
  ab_copy_agent: "Copy Agent",
  ab_add_worker: "Add Worker",
  ab_connect: "Connect Workers",
  ab_set_parameters: "Set Parameters",
  ab_set_field_value: "Set Field Value",
  ab_add_condition: "Add Condition",
  ab_add_schema_field: "Add Schema Field",
  ab_reset_conversation: "New Test Conversation",
  ab_run_agent: "Test Run",
  ab_remove_schema_field: "Remove Schema Field",
  ab_disconnect: "Disconnect Workers",
  ab_remove_worker: "Remove Worker",
}

// ── Agent settings: escalation flags and the bound form ─────────────────────

interface SettingsContext {
  getAgent: () => Agent
  teamId?: string
}

interface FlagArgs {
  id: string | null
  label: string | null
  detectionPrompt: string | null
  icon: string | null
  enabled: boolean | null
}

// Tells the editor's settings dialog to reload what it shows
const announce = () => window.dispatchEvent(new Event(AGENT_SETTINGS_CHANGED_EVENT))

async function teamForms(teamId?: string): Promise<{ id: string; title: string }[]> {
  if (!teamId) return []
  const { data, error } = await supabase.from("forms").select("id, title").eq("team", teamId)
  if (error) throw new Error(`Couldn't load the team's forms: ${error.message}`)
  return (data ?? []).map((f: any) => ({ id: String(f.id), title: f.title || "(untitled)" }))
}

// Why a writeFlags flag_id isn't one of the agent's flags, or null
export function checkFlagParameter(agent: Agent, workerType: string | undefined, parsed: Record<string, any>): string | null {
  if (workerType !== "writeFlags" || parsed.flag_id == null || parsed.flag_id === "") return null
  const ids = flagsOf(agent).map(f => f.id)
  if (ids.includes(parsed.flag_id)) return null
  return `"${parsed.flag_id}" isn't one of this agent's flags (${ids.join(", ")}). `
    + "Add it with ab_set_flag first, or use one of those ids."
}

export function checkSetFlag(agent: Agent, a: FlagArgs): string | null {
  const flags = flagsOf(agent)
  if (a.icon != null && !FLAG_ICONS.includes(a.icon)) return `icon must be one of: ${FLAG_ICONS.join(", ")}.`
  if (a.id == null) {
    if (flags.length >= MAX_FLAGS) return `An agent has at most ${MAX_FLAGS} flags. Remove a custom flag first.`
    if (!a.label?.trim() || !a.detectionPrompt?.trim()) return "A new flag needs a label and a detection prompt."
    return null
  }
  const flag = flags.find(f => f.id === a.id)
  if (!flag) return `No flag "${a.id}". This agent's flags: ${flags.map(f => f.id).join(", ")}.`
  if (flag.type === "builtin" && a.icon != null && a.icon !== flag.icon) return "Built-in flags keep their icon."
  if (a.label != null && !a.label.trim()) return "label can't be empty."
  return null
}

export function checkRemoveFlag(agent: Agent, id: string): string | null {
  const flag = flagsOf(agent).find(f => f.id === id)
  if (!flag) return `No flag "${id}".`
  if (flag.type === "builtin") return `${flag.label} is built in and can't be removed. Switch it off with ab_set_flag (enabled: false).`
  return null
}

export async function checkBindForm(teamId: string | undefined, formId: string | null): Promise<string | null> {
  if (formId == null) return null
  const forms = await teamForms(teamId)
  if (forms.some(f => f.id === formId)) return null
  return `No form "${formId}" in this team. Use an id from ab_get_agent_settings().teamForms`
    + (forms.length ? `: ${forms.slice(0, 10).map(f => `${f.id} (${f.title})`).join(", ")}.` : "; the team has no forms yet.")
}

export function makeAgentSettingsTools(ctx: SettingsContext) {
  return {
    ab_get_agent_settings: tool({
      description:
        "Read the open agent's escalation flags and its bound Magic Form, plus every form the team has. "
        + "Read this before changing flags, binding a form, or setting a writeFlags worker's flag_id.",
      parameters: z.object({}),
      execute: async () => {
        const agent = ctx.getAgent()
        const forms = await teamForms(ctx.teamId)
        return {
          flags: flagsOf(agent),
          maxFlags: MAX_FLAGS,
          flagIcons: FLAG_ICONS,
          boundForm: (agent.forms ?? []).map(id => forms.find(f => f.id === id) ?? { id, title: "(not in this team)" })[0] ?? null,
          teamForms: forms,
        }
      },
    }),

    ab_set_flag: tool({
      description:
        "Add a custom escalation flag, or change one: label, detection prompt, icon, on/off. Flags are checked on every "
        + "message and shown to moderators; a writeFlags worker can also raise one by id. Built-ins (high_risk, "
        + "low_confidence, asked_human) can be edited or switched off, not removed. Only the fields you pass change.",
      parameters: z.object({
        id: z.string().nullable().describe("Flag to change. Null adds a new custom flag"),
        label: z.string().nullable(),
        detectionPrompt: z.string().nullable()
          .describe("When to raise it, e.g. \"Determine if the user mentions losing their documents.\""),
        icon: z.string().nullable().describe(`Custom flags only. One of: ${FLAG_ICONS.join(", ")}`),
        enabled: z.boolean().nullable(),
      }),
      execute: async (a: FlagArgs) => {
        const agent = ctx.getAgent()
        const invalid = checkSetFlag(agent, a)
        if (invalid) return { error: invalid }
        const flags = flagsOf(agent).map(f => ({ ...f }))
        let flag: AgentEscalationFlag
        if (a.id == null) {
          flag = {
            id: `custom_${Date.now()}_${flags.length}`,
            type: "custom",
            label: a.label!.trim(),
            icon: a.icon ?? "flag",
            detection_prompt: a.detectionPrompt!.trim(),
            enabled: a.enabled ?? true,
          }
          flags.push(flag)
        } else {
          flag = flags.find(f => f.id === a.id)!
          if (a.label != null) flag.label = a.label.trim()
          if (a.detectionPrompt != null) flag.detection_prompt = a.detectionPrompt.trim()
          if (a.icon != null) flag.icon = a.icon
          if (a.enabled != null) flag.enabled = a.enabled
        }
        agent.escalationFlags = flags
        announce()
        return { ok: true, flag, summary: `${a.id == null ? "Added" : "Updated"} flag "${flag.label}" (${flag.id}). Save the agent to keep it.` }
      },
    }),

    ab_remove_flag: tool({
      description: "Remove a custom escalation flag. Built-in flags can only be switched off.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const agent = ctx.getAgent()
        const invalid = checkRemoveFlag(agent, id)
        if (invalid) return { error: invalid }
        const flags = flagsOf(agent)
        const removed = flags.find(f => f.id === id)!
        agent.escalationFlags = flags.filter(f => f.id !== id)
        const users = Object.values<any>(agent.workers ?? {}).filter(w => w.config?.type === "writeFlags" && w.parameters?.flag_id === id)
        announce()
        return {
          ok: true,
          summary: `Removed flag "${removed.label}". Save the agent to keep it.`,
          warning: users.length ? `${users.length} writeFlags worker(s) still point at ${id}: ${users.map(w => w.config.id).join(", ")}.` : undefined,
        }
      },
    }),

    ab_bind_form: tool({
      description:
        "Bind a Magic Form to the open agent, replacing any bound one (an agent has at most one), or unbind with null. "
        + "A bound form is filled from the conversation after every turn. Its fields need an AI fill instruction or keyword "
        + "rule to be extracted: check them with get_form. To make a new form, use create_form first.",
      parameters: z.object({
        formId: z.string().nullable().describe("A form id from ab_get_agent_settings().teamForms, or null to unbind"),
      }),
      execute: async ({ formId }) => {
        const invalid = await checkBindForm(ctx.teamId, formId)
        if (invalid) return { error: invalid }
        const agent = ctx.getAgent()
        agent.forms = formId ? [formId] : []
        announce()
        return { ok: true, summary: `${formId ? `Bound form ${formId}` : "Unbound the form"}. Save the agent to keep it.` }
      },
    }),
  }
}

export const AGENT_SETTINGS_TOOL_LABELS: Record<string, string> = {
  ab_get_agent_settings: "Read agent settings",
  ab_set_flag: "Set escalation flag",
  ab_remove_flag: "Remove escalation flag",
  ab_bind_form: "Bind form",
}
